import React, { createContext, useEffect, useMemo, useState } from 'react';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { WebFrameFilters } from '../interfaces/WebFrameFilters';
import { compareQueryParams, getQueryParams } from '../utils/queryParams';

const WebFrameFilterContext = createContext<WebFrameFilters>({});

interface IProps {
  redirectUrlOnChange?: string;
  onChange?: (filters: WebFrameFilters) => void;
  cancelRedirectCondition?: () => boolean;
  children?: React.ReactNode;
}

export const WebFrameFilterProvider: React.FC<IProps> = ({
  children,
  redirectUrlOnChange,
  onChange,
  cancelRedirectCondition,
}) => {
  const history = useHistory();
  const [params, setParams] = useState<WebFrameFilters>(
    getQueryParams(history.location)
  );

  const encodedParam = useMemo(() => {
    const encodeParam = { ...params };
    Object.keys(encodeParam).forEach((k) => {
      encodeParam[k.toLowerCase()] = encodeURIComponent(
        encodeParam[k.toLowerCase()]
      );
    });
    return encodeParam;
  }, [params]);

  useEffect(() => {
    return history.listen((location: any) => {
      const newParams = getQueryParams(location);

      if (!compareQueryParams(params, newParams)) {
        setParams(newParams);

        if (
          redirectUrlOnChange &&
          location.pathname.startsWith(redirectUrlOnChange) &&
          (!cancelRedirectCondition || !cancelRedirectCondition())
        ) {
          // Using the timeout because otherwise we can reach an infinite loop
          // because history.push and history.listen is faster than setting
          // setParams in above lines
          setTimeout(() => {
            history.push(redirectUrlOnChange);
          }, 100);
        }

        onChange?.(newParams);
      }
    });
  }, [cancelRedirectCondition, history, onChange, params, redirectUrlOnChange]);

  return (
    <WebFrameFilterContext.Provider value={encodedParam}>
      {children}
    </WebFrameFilterContext.Provider>
  );
};

export const useWebFrameFilters = () => useContext(WebFrameFilterContext);
